import { GroupKind, type JSONConfig, MeasurementKind, ObjectsKind, TuneControlsKind } from '@/engine/app/interfaces.ts';
import * as defaultObjectModel from './_viewObjects.config.ts';
import * as defaultViewOptionsModel from './_viewOptions.config.ts';
import * as defaultViewOptionsGroups from './_viewOptionsGroup.config.ts';
import { viewPorts } from '@/surgeries/cardiac/TTVR/configs/views/_common.config.ts';
import { heartWorkflowIndications } from './_wfIndications.config.ts';
import { axial, coronal, sagittal } from '@/engine/common/imageSlices.config.ts';
import { SliceType } from '@/engine/app/modules/images/interfaces.ts';

export const config: JSONConfig.View = {
  descriptor: 'anatomy',
  label: 'Anatomy',
  sideLabel: 'Right heart assessment',
  imageStackDescriptor: 'Heart',
  imagesRoiConfig: {
    descriptor: 'device_roi',
    syncSliceDescriptor: SliceType.SAGITTAL,
    zoomFactor: 0.5
  },
  viewOrientationDescriptor: 'heart_orientation',
  viewOptions: [
    {
      ...defaultViewOptionsGroups.devices,
      children: [
        defaultViewOptionsModel.valve,
        defaultViewOptionsModel.catheter,
        defaultViewOptionsModel.additionalDevice,
        defaultViewOptionsModel.additionalStructure
      ]
    },
    {
      ...defaultViewOptionsGroups.rightHeart,
      children: [
        defaultViewOptionsModel.rightAtrium,
        defaultViewOptionsModel.rightVentricle,
        defaultViewOptionsModel.rightHeart,
        defaultViewOptionsModel.pulmonaryArtery,
        defaultViewOptionsModel.calcification
      ]
    },
    {
      ...defaultViewOptionsGroups.leftHeart,
      children: [
        defaultViewOptionsModel.leftAtrium,
        defaultViewOptionsModel.leftVentricle,
        defaultViewOptionsModel.aorta
      ]
    }
  ],
  viewPorts,
  imageSlices: [axial, coronal, sagittal],
  objects: [
    { ...defaultObjectModel.landingZoneSpline, isSelected: true, isPickable: true, isTunable: true },
    { ...defaultObjectModel.basalRvAPoint, isPickable: true, isTunable: true },
    { ...defaultObjectModel.basalRvPPoint, isPickable: true, isTunable: true },
    { ...defaultObjectModel.basalRvSPoint, isPickable: true, isTunable: true },
    { ...defaultObjectModel.basalRvLPoint, isPickable: true, isTunable: true },
    { ...defaultObjectModel.basalRvTvAPoint, isPickable: true, isTunable: true },
    { ...defaultObjectModel.basalRvTvPPoint, isPickable: true, isTunable: true },
    { ...defaultObjectModel.basalRvTvSPoint, isPickable: true, isTunable: true },
    { ...defaultObjectModel.basalRvTvLPoint, isPickable: true, isTunable: true },
    defaultObjectModel.valve,
    defaultObjectModel.catheter,
    defaultObjectModel.leftAtrium,
    defaultObjectModel.leftVentricle,
    defaultObjectModel.rightAtrium,
    defaultObjectModel.rightVentricle,
    defaultObjectModel.rightHeart,
    defaultObjectModel.aorta,
    defaultObjectModel.pulmonaryArtery,
    defaultObjectModel.eustachianRidgeSpline,
    defaultObjectModel.additionalDevice,
    defaultObjectModel.additionalStructure,
    defaultObjectModel.veinsCalcification,
    defaultObjectModel.calcification
  ],
  centerLines: [
    {
      descriptor: 'long_axis.centerline',
      isVisible: false
    }
  ],
  measurements: [
    {
      descriptor: 'landing_zone_area',
      isPickable: false,
      isTunable: false,
      isVisible: true,
      isSelected: true,
      isClippable: false
    },
    {
      descriptor: 'var_basal_rv_ap',
      isPickable: false,
      isTunable: false,
      isVisible: false,
      zoomFactor: 0.7
    },
    {
      descriptor: 'var_basal_rv_sl',
      isPickable: false,
      isTunable: false,
      isVisible: false,
      zoomFactor: 0.7
    },
    {
      descriptor: 'var_basal_rv_tv_ap',
      isPickable: false,
      isTunable: false,
      isVisible: false,
      zoomFactor: 0.7
    },
    {
      descriptor: 'var_basal_rv_tv_sl',
      isPickable: false,
      isTunable: false,
      isVisible: false,
      zoomFactor: 0.7
    },
    {
      descriptor: 'var_papillary_distribution'
    },
    {
      descriptor: 'var_papillary_heights'
    },
    {
      descriptor: 'tp.pl_svc1.area',
      isSelected: false,
      isPickable: false,
      isVisible: false
    },
    {
      descriptor: 'tp.pl_ivc1.area',
      isSelected: false,
      isPickable: false,
      isVisible: false
    },
    {
      descriptor: 'var_ra_height',
      isVisible: false,
      zoomFactor: 0.8
    },
    {
      descriptor: 'var_rv_depth',
      isVisible: false,
      zoomFactor: 0.8
    },
    {
      descriptor: 'var_rv_depth_apex',
      isVisible: false,
      zoomFactor: 0.8
    },
    {
      descriptor: 'var_ivc_to_tva',
      isVisible: false,
      zoomFactor: 0.8
    },
    {
      descriptor: 'var_ivc_to_tva_offset',
      isVisible: false,
      zoomFactor: 0.8
    }
  ],
  measurementBoxes: [
    {
      kind: MeasurementKind.WideArea,
      groupDescriptor: 'annulus',
      label: '',
      entries: ['landing_zone_area']
    },
    {
      kind: MeasurementKind.Distance,
      groupDescriptor: 'basal.rv',
      label: 'Basal RV at 5 mm',
      entries: ['var_basal_rv_ap', 'var_basal_rv_sl']
    },
    {
      kind: MeasurementKind.Distance,
      groupDescriptor: 'basal.rv',
      label: 'Basal RV custom',
      entries: ['var_basal_rv_tv_ap', 'var_basal_rv_tv_sl']
    },
    {
      kind: MeasurementKind.VariableDistance,
      groupDescriptor: 'papillary',
      label: 'Papillary Muscles',
      entries: [],
      details: ['devicePlacement', 'delivery', 'intra-op']
    },
    {
      kind: MeasurementKind.VariableDistance,
      groupDescriptor: 'leaflet',
      label: 'Leaflet excursion',
      entries: [],
      details: ['devicePlacement', 'delivery', 'intra-op']
    },
    {
      kind: MeasurementKind.Flag,
      groupDescriptor: 'papillary',
      label: '',
      entries: ['var_papillary_heights'],
      hasCriteria: true
    },
    {
      kind: MeasurementKind.Value,
      groupDescriptor: 'papillary',
      label: '',
      entries: ['var_papillary_distribution'],
      hasCriteria: true
    }
  ],
  objectBoxes: [
    {
      kind: ObjectsKind.CommissurePoints,
      groupDescriptor: 'commissure',
      label: 'Commissure Points',
      entries: [],
      details: ['devicePlacement', 'delivery', 'ascending_aorta', 'intra-op']
    }
  ],
  workflowIndications: [...heartWorkflowIndications],
  orientationPresets: [],
  tuneControls: [
    {
      kind: TuneControlsKind.Stepper,
      groupDescriptor: 'basal.rv',
      label: 'Custom plane depth',
      units: {
        value: 'mm'
      },
      descriptor: 'tp.basal_rv_tv.translation',
      locatableDescriptors: ['var_basal_rv_tv_ap', 'var_basal_rv_tv_sl'],
      hasReset: true
    }
  ],
  planningEntitiesGroups: [
    {
      descriptor: 'annulus',
      label: 'Annulus',
      kind: GroupKind.Collapsible,
      isExpanded: true
    },
    {
      descriptor: 'basal.rv',
      label: 'Basal RV',
      kind: GroupKind.Collapsible
    },
    {
      descriptor: 'leaflet',
      label: 'Leaflet excursion',
      kind: GroupKind.Collapsible
    },
    {
      descriptor: 'papillary',
      label: 'Papillary muscles',
      kind: GroupKind.Collapsible
    },
    {
      descriptor: 'commissure',
      label: 'Commissure points',
      kind: GroupKind.Collapsible
    }
  ],
  visibilityDependencies: [
    {
      item: 'landing_zone_spline',
      dependsOn: ['landing_zone_area']
    }
  ],
  selectionDependencies: [
    {
      item: 'tp.pt_basal_rv_a',
      dependsOn: ['var_basal_rv_ap']
    },
    {
      item: 'tp.pt_basal_rv_p',
      dependsOn: ['var_basal_rv_ap']
    },
    {
      item: 'tp.pt_basal_rv_s',
      dependsOn: ['var_basal_rv_sl']
    },
    {
      item: 'tp.pt_basal_rv_l',
      dependsOn: ['var_basal_rv_sl']
    },
    {
      item: 'tp.pt_basal_rv_tv_a',
      dependsOn: ['var_basal_rv_tv_ap']
    },
    {
      item: 'tp.pt_basal_rv_tv_p',
      dependsOn: ['var_basal_rv_tv_ap']
    },
    {
      item: 'tp.pt_basal_rv_tv_s',
      dependsOn: ['var_basal_rv_tv_sl']
    },
    {
      item: 'tp.pt_basal_rv_tv_l',
      dependsOn: ['var_basal_rv_tv_sl']
    }
  ]
};
